import { useEffect, useState } from 'react';

import {
    useDeleteAllFiles,
    useDeleteFile,
    useFiles,
} from '@/features/file-overview/api';
import { FileListItem } from '@/features/file-overview/file-list-item';
import { FileTypeFilter } from '@/features/file-overview/file-type-filter';
import { PreviewDrawer } from '@/features/file-overview/preview-drawer';
import { PreviewDrawerWithTabs } from '@/features/file-overview/preview-drawer-with-tabs';
import { FileType, FileWithLinks } from '@/features/file-overview/types';
import { UnifiedUploadArea } from '@/features/maintenance/unified-upload-area';
import { createFileRoute } from '@tanstack/react-router';
import { FileX2, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { usePagination } from '@/hooks/usePagination';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

export const Route = createFileRoute('/file-overview')({
    component: DocumentOverview,
});

const LIMIT = 8;

function DocumentOverview() {
    const [active, setActive] = useState<FileWithLinks | null>(null);
    const [total, setTotal] = useState(0);
    const [selectedType, setSelectedType] = useState<FileType | null>(null);
    const { limit, skip, Pagination } = usePagination({
        totalElements: total,
        limit: LIMIT,
    });
    const { data: files, isLoading } = useFiles({
        limit,
        skip,
        filter: selectedType,
    });

    const deleteFiles = useDeleteFile();
    const deleteAllFiles = useDeleteAllFiles();

    const hasFiles = files !== undefined && files.total > 0;
    useEffect(() => {
        if (files) {
            setTotal(files.total);
        }
    }, [files]);

    const handleTypeSelect = (type: FileType | null) => setSelectedType(type);

    const handleDocumentClick = (id: number) => {
        const currentFile = files?.results.find((f) => f.id === id);
        if (
            currentFile &&
            (currentFile.type === FileType.sb ||
                currentFile.type == FileType.eo)
        ) {
            setActive(currentFile);
        }
    };

    const handleFileDelete = (fileId: string | number) =>
        toast.promise(deleteFiles.mutateAsync({ id: fileId }), {
            loading: 'Deleting file...',
            success: 'File deleted',
        });

    const handleDeleteAllClick = () =>
        toast.promise(deleteAllFiles.mutateAsync, {
            loading: 'Deleting all files...',
            success: 'All files deleted',
        });

    const handleReparseClick = async () =>
        //TODO: Add reparse all files
        // toast.promise(reparseDocuments.mutateAsync, {
        //     loading: 'Starting re-index process',
        //     success: 'Please wait for the server to re-index all SBs',
        // });
        {};

    return (
        <div className="flex-1 bg-white min-h-full" data-testid="FileOverview">
            {/* Drawer showing active SB after click */}
            {active && active.type === FileType.sb && (
                <PreviewDrawer
                    file={active}
                    open={!!active}
                    onOpenChange={() => setActive(null)}
                />
            )}

            {/* Drawer showing active EO after click */}
            {active && active.type === FileType.eo && (
                <PreviewDrawerWithTabs
                    file={active}
                    open={!!active}
                    onOpenChange={() => setActive(null)}
                />
            )}

            <div className="p-8 flex flex-col gap-4 mx-auto max-w-[1600px]">
                {/* Simple menu bar */}
                <div className="flex flex-row gap-2 my-4 items-center">
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button className="flex flex-row gap-2">
                                <Upload className="w-4 h-4" />
                                Upload
                            </Button>
                        </SheetTrigger>
                        <SheetContent className="overflow-y-auto">
                            <UnifiedUploadArea />
                        </SheetContent>
                    </Sheet>
                    {hasFiles && (
                        <>
                            <Button
                                variant="ghost"
                                className="flex flex-row gap-2 text-red-600"
                                onClick={handleDeleteAllClick}
                            >
                                <FileX2 className="w-4 h-4" />
                                Delete All
                            </Button>
                        </>
                    )}
                </div>

                {!isLoading && (
                    <FileTypeFilter
                        selectedType={selectedType}
                        onTypeSelect={handleTypeSelect}
                    />
                )}

                {/* List of all SBs or an empty state */}
                <div className="p-8 flex flex-col gap-2">
                    {isLoading ? (
                        <div className="text-center text-gray-600 py-8">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                            <p className="text-lg font-medium">
                                Loading files...
                            </p>
                        </div>
                    ) : hasFiles ? (
                        files.results.map((f) => (
                            <FileListItem
                                key={f.id}
                                file={f}
                                onDelete={() => handleFileDelete(f.id)}
                                onClick={() => handleDocumentClick(f.id)}
                            />
                        ))
                    ) : (
                        <div className="text-center text-gray-600 py-8">
                            <p className="text-lg font-medium mb-2">
                                No files found.
                            </p>
                            <p>
                                Please upload Files from your local computer to
                                get started.
                            </p>
                        </div>
                    )}
                </div>

                {/* Render pagination only if there are documents */}
                {hasFiles && <Pagination />}
            </div>
        </div>
    );
}
